import LaunchIcon from '@mui/icons-material/Launch';
import { Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { CURRENCY_PARAMS, NUMBER_PARAMS } from '@work4all/components';
import { EditableCell } from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/components/editable-cell/EditableCell';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { currencyAsSign, parseAsFloat } from '@work4all/utils';

import {
  EditTableColumns,
  OnEditPosition,
} from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';
import { GroupedPosition } from '../../types';
import { SupplierPricePickerField } from '../SupplierPricePicker/SupplierPricePickerField';

export const makeSupplierColumn = (
  t: TFunction,
  onEditPosition: (result: OnEditPosition<GroupedPosition>) => void,
  width = 98,
  hideSupplierWhenChild = false,
  readOnly = false
): EditTableColumns<GroupedPosition> => ({
  Header: (
    <div style={{ display: 'flex', gap: 2 }}>
      {t('COMMON.SUPPLIER')}
      <Typography color="error">*</Typography>
    </div>
  ),
  accessor: 'supplier',
  type: 'text',
  width,
  Cell: (cell) => {
    if (
      !cell.row.original.articleId ||
      (cell.row.original.posId && hideSupplierWhenChild)
    )
      return <></>;
    return (
      <SupplierPricePickerField
        disabled={readOnly}
        onChange={(item) => {
          onEditPosition({
            forceUpdate: true,
            position: {
              localId: cell.row.id,
              id: cell.row.original.id,
              singlePriceNet: item.purchasePriceListPrice
                ? Math.max(item.purchasePriceListPrice, 0)
                : Math.max(item.price, 0),
              supplier: item.supplier,
              supplierId: item.supplierId || item.supplier?.id,
              currency: item.currency,
              discount: item.purchasePriceDiscount,
            },
          });
        }}
        articleId={cell.row.original.articleId}
        value={cell.value as { id: number; name: string }}
      />
    );
  },
});

export const makeDiscountColumn = (
  t: TFunction,
  onEditPosition: (result: OnEditPosition<GroupedPosition>) => void,
  contractToDeliveryNote = false,
  readOnly = false
): EditTableColumns<GroupedPosition> => ({
  Header: t('FIELDS.discount') + ` %`,
  accessor: 'discount',
  width: remToPx(7),
  type: 'number',
  transformInputValue: (input: string) => {
    const number = parseAsFloat(input);
    if (number < 0) return '0';
    if (number > 100) return '100';
    return input;
  },
  ...NUMBER_PARAMS,
  Cell: (cell) => {
    return (
      <ConversionCell
        cell={cell}
        onEditPosition={onEditPosition}
        contractToDeliveryNote={contractToDeliveryNote}
        readOnly={readOnly}
      />
    );
  },
});

export const makeUnitColumn = (
  t: TFunction,
  width = 56,
  onEditPosition: (result: OnEditPosition<GroupedPosition>) => void = () => {},
  contractToDeliveryNote = false,
  readOnly = false
): EditTableColumns<GroupedPosition> => ({
  Header: t('COMMON.ERP.UNIT'),
  accessor: 'unit',
  type: 'text',
  width,
  Cell: (cell) => {
    return (
      <ConversionCell
        cell={cell}
        onEditPosition={onEditPosition}
        contractToDeliveryNote={contractToDeliveryNote}
        readOnly={readOnly}
      />
    );
  },
});

export const makeDescriptionColumn = (
  t: TFunction,
  width = 252,
  header: string = undefined
): EditTableColumns<GroupedPosition> => ({
  Header: header ?? t('COMMON.ERP.DESCRIPTION'),
  accessor: 'longtext',
  type: 'text',
  width,
});

export const makePriceColumn = (
  t: TFunction,
  formatTitleWithCurrency: (title: string) => string = (x) => x,
  width = 98,
  showCurrencyInRow = false,
  onEditPosition?: (result: OnEditPosition<GroupedPosition>) => void,
  contractToDeliveryNote = false
): EditTableColumns<GroupedPosition> => ({
  Header: onEditPosition ? (
    <div style={{ display: 'flex' }}>
      {formatTitleWithCurrency(t('COMMON.ERP.PRICE'))}
      <sup>1</sup>
    </div>
  ) : (
    formatTitleWithCurrency(t('COMMON.ERP.PRICE'))
  ),
  accessor: 'singlePriceNet',
  width,
  type: 'number',
  Cell: (cell) => {
    if (onEditPosition)
      return (
        <ConversionCell
          cell={cell}
          onEditPosition={onEditPosition}
          contractToDeliveryNote={contractToDeliveryNote}
        />
      );

    if (showCurrencyInRow)
      return (
        <EditableCell
          unit={
            cell.value
              ? currencyAsSign(cell.row.original.currency?.name)
              : undefined
          }
          {...cell}
        />
      );
    else return <EditableCell {...cell} />;
  },
  ...CURRENCY_PARAMS,
});

export const makeTotalPriceColumn = (
  t: TFunction,
  formatTitleWithCurrency: (title: string) => string = (x) => x,
  width = 98,
  showCurrencyInRow = false
): EditTableColumns<GroupedPosition> => ({
  Header: formatTitleWithCurrency(t('COMMON.ERP.TOTAL_PRICE')),
  accessor: 'totalPriceNet',
  type: 'number',
  width,
  Cell: (cell) => {
    if (showCurrencyInRow)
      return (
        <EditableCell
          unit={
            cell.value
              ? currencyAsSign(cell.row.original.currency?.name)
              : undefined
          }
          {...cell}
        />
      );
    else return <EditableCell {...cell} />;
  },
  ...CURRENCY_PARAMS,
});

export const makeInsteadOfPriceColumn = (
  t: TFunction
): EditTableColumns<GroupedPosition> => ({
  Header: t('COMMON.ERP.INSTEAD_OF_PRICE'),
  accessor: 'insteadOfTotalPrice',
  type: 'text',
  width: 168,
});

export const makeNavigateColumn = (
  onClick: (position: Position) => void
): EditTableColumns<GroupedPosition> => {
  return {
    Header: '',
    accessor: 'id',
    type: 'text',
    width: 52,
    Cell: (cell) => {
      if (cell.row.original.positionKind === ErpPositionsKind.STUECKLISTE)
        return <></>;
      return (
        <LaunchIcon
          style={{
            cursor: 'pointer',
          }}
          color="primary"
          onClick={() => onClick(cell.row.original)}
        />
      );
    },
  };
};

export const makeNumberColumn = (
  t: TFunction,
  width = 52
): EditTableColumns<GroupedPosition> => ({
  Header: t('COMMON.ERP.NUMBER'),
  accessor: 'number',
  width,
});

export const makeShouldColumn = (
  t: TFunction,
  className: string,
  isNumber: boolean,
  width = 70
): EditTableColumns<GroupedPosition> => ({
  Header: t('COMMON.ERP.SHOULD'),
  accessor: 'should',
  type: isNumber ? 'number' : undefined,
  width,
  className,
  ...NUMBER_PARAMS,
});

export const makeArticleNumberColumn = (
  t: TFunction,
  width = 70
): EditTableColumns<GroupedPosition> => ({
  Header: t('COMMON.ERP.ARTICLE_NUMBER_CONVERSION'),
  accessor: 'articleNumber',
  width,
  ...NUMBER_PARAMS,
});

export const makeAmountColumn = (
  t: TFunction,
  onEditPosition?: (result: OnEditPosition<Position>) => void,
  width = 84,
  childsReadOnly = false,
  readOnly = false,
  contractToDeliveryNote = false
): EditTableColumns<GroupedPosition> => ({
  Header: t('COMMON.ERP.AMOUNT'),
  accessor: 'amount',
  width,
  type: 'number',
  transformInputValue: (input: string) => {
    const number = parseAsFloat(input);
    if (number < 0) return '0';
    return input;
  },
  ...NUMBER_PARAMS,
  Cell: (cell) => {
    return (
      <ConversionCell
        cell={cell}
        onEditPosition={onEditPosition}
        childsReadOnly={childsReadOnly}
        readOnly={readOnly}
        contractToDeliveryNote={contractToDeliveryNote}
      />
    );
  },
});

interface NumberConversionCellProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cell: any;
  onEditPosition: (result: OnEditPosition<GroupedPosition>) => void;
  childsReadOnly?: boolean;
  readOnly?: boolean;
  contractToDeliveryNote?: boolean;
}

const ConversionCell = (props: NumberConversionCellProps) => {
  const {
    cell,
    onEditPosition,
    childsReadOnly,
    readOnly,
    contractToDeliveryNote,
  } = props;
  const {
    i18n: { language },
  } = useTranslation();
  if (
    [
      ErpPositionsKind.TITEL,
      ErpPositionsKind.TITELSUMME,
      ErpPositionsKind.UMWANDLUNGSHISTORIE,
    ].includes(cell.row.original.positionKind)
  )
    return '';

  if (
    (cell.row.original.positionKind !== ErpPositionsKind.STANDARD &&
      cell.row.original.positionKind !== ErpPositionsKind.STUECKLISTE) ||
    (contractToDeliveryNote && cell.row.original.posId) ||
    !onEditPosition
  ) {
    if (!cell.value) return '';
    return <EditableCell {...cell} />;
  }

  if ((childsReadOnly && cell.row.original.isGroupedChild) || readOnly)
    return <EditableCell {...cell} />;

  return (
    <EditableCell
      {...(cell as object)}
      isEditable
      isEditMode
      onChange={(value: string) => {
        let finalValue: string | number = value;
        if (cell.type === 'number') {
          const parsedValue = parseAsFloat(value, language) || 0;
          finalValue = Math.max(parsedValue, 0);
        }
        onEditPosition?.({
          forceUpdate: true,
          position: {
            localId: cell.row.id,
            id: cell.row.original.id,
            [cell.accessor]: finalValue,
          },
        });
      }}
    />
  );
};
